import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  KeyRound,
  RefreshCw,
  Sprout,
  Activity,
  UserCog,
  Lock,
  LogOut,
  Pause,
  Play,
} from 'lucide-react';
import { Card, CardTitle, CardDescription, Button, Input, Skeleton, Stat } from '@/components/ui';
import { api } from '@/lib/api';
import { formatNumber, MAX_KEYWORDS, type UserRole, type MemberSummary } from '@keywordgod/shared';
import { cn } from '@/lib/utils';
import { ROLE_COLOR, ROLE_LABEL, STATUS_COLOR, STATUS_LABEL } from '@/lib/constants';

interface PoolEntry {
  index: number;
  blocked: string | null;
  usedToday?: number;
}
interface PoolStatus {
  ads: PoolEntry[];
  search: PoolEntry[];
}
interface SeedReport {
  startedAt: number;
  finishedAt: number;
  durationMs: number;
  seedsInserted: number;
  totalInserted: number;
  apiCalls: number;
  depthReached: number;
  errors: number;
  capHit: boolean;
}

/** cookie 인증으로 /api/admin/* 호출 */
async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const json = (await res.json().catch(() => null)) as
    | { success: true; data: T }
    | { success: false; error: { code: string; message: string } }
    | null;
  if (!json || !('success' in json)) throw new Error(`bad response (${res.status})`);
  if (!json.success) {
    const err = new Error(json.error.message);
    (err as Error & { code?: string }).code = json.error.code;
    throw err;
  }
  return json.data;
}

export function Admin() {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => api.adminAuth.me(),
    refetchInterval: 60_000,
  });

  // 로그아웃 핸들러
  const logout = useMutation({
    mutationFn: () => api.adminAuth.logout(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  });

  if (me.isLoading) return null;
  if (!me.data?.authenticated) {
    return <AdminLogin onSuccess={() => qc.invalidateQueries({ queryKey: ['admin'] })} />;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold flex items-center gap-2">
            <Shield className="h-7 w-7 text-gold" />
            <span className="text-gold-gradient">관리자 콘솔</span>
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            운영진 임명 · 비밀번호 변경 · 키 풀 · 큐잉 · 시드 수집기
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => logout.mutate()}>
          <LogOut className="h-4 w-4" /> 관리자 로그아웃
        </Button>
      </header>

      <CollectionPanel />
      <PasswordPanel />
      <ModeratorPanel />
      <PoolPanel />
      <RefreshPanel />
      <SeedPanel />
    </div>
  );
}

// ─── 로그인 화면 ────────────────────────────────────────────

function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: (pw: string) => api.adminAuth.login(pw),
    onSuccess: (d) => {
      setErr(null);
      if (d.bootstrapped) {
        alert('첫 로그인이 감지되어 환경변수 토큰을 비밀번호로 등록했습니다. 비밀번호 변경 패널에서 즉시 변경하세요.');
      }
      onSuccess();
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <div className="max-w-md mx-auto py-10">
      <Card>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-gold" />
          관리자 로그인
        </CardTitle>
        <CardDescription>
          Google 로그인과는 별개의 인증입니다. 비밀번호로 로그인하세요.
        </CardDescription>
        <form
          className="mt-4 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const pw = password.trim();
            if (pw) login.mutate(pw);
          }}
        >
          <Input
            type="password"
            placeholder="관리자 비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          <Button type="submit" disabled={!password.trim() || login.isPending}>
            {login.isPending ? '확인 중…' : '로그인'}
          </Button>
        </form>
        {err && <p className="mt-3 text-sm text-red-400">{err}</p>}
        <p className="mt-4 text-xs text-gray-500">
          처음 로그인이면 환경변수 <code>ADMIN_TOKEN</code> 값을 비밀번호로 입력 →
          로그인 후 즉시 변경하세요.
        </p>
      </Card>
    </div>
  );
}

// ─── 수집 토글 + 진행률 (Phase 14) ──────────────────────────

function CollectionPanel() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['admin', 'collection'],
    queryFn: () => api.adminAuth.collectionStatus(),
    refetchInterval: 5_000,
  });

  const pause = useMutation({
    mutationFn: () => api.adminAuth.collectionPause(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'collection'] });
      qc.invalidateQueries({ queryKey: ['stats', 'keywords'] });
    },
  });
  const resume = useMutation({
    mutationFn: () => api.adminAuth.collectionResume(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'collection'] });
      qc.invalidateQueries({ queryKey: ['stats', 'keywords'] });
    },
  });

  const total = status.data?.total ?? 0;
  const max = status.data?.max ?? MAX_KEYWORDS;
  const paused = status.data?.paused ?? false;
  const pct = max > 0 ? Math.min(100, (total / max) * 100) : 0;
  const busy = pause.isPending || resume.isPending;

  return (
    <Card className={cn('border-gold/40', paused && 'border-amber-400/40')}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-gold" /> 키워드 수집 상태
          </CardTitle>
          <CardDescription>
            누적된 키워드 수와 진행률. 일시정지 시 분석 결과의 새 키워드 자동 누적이 멈춥니다 (이미 큐에 있는 분석은 계속).
          </CardDescription>
        </div>
        {paused ? (
          <Button variant="gold" onClick={() => resume.mutate()} disabled={busy}>
            <Play className="h-4 w-4" /> 수집 재개
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => pause.mutate()} disabled={busy}>
            <Pause className="h-4 w-4" /> 수집 일시정지
          </Button>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="현재 누적" value={formatNumber(total)} />
        <Stat label="목표 (상한)" value={formatNumber(max)} />
        <Stat label="진행률" value={`${pct.toFixed(3)}%`} />
      </div>

      <div className="mt-4 h-2 rounded-full bg-gray-800 overflow-hidden">
        <div
          className={cn(
            'h-full transition-[width] duration-700',
            paused
              ? 'bg-gradient-to-r from-amber-500 to-amber-300'
              : 'bg-gradient-to-r from-[#FFA500] to-[#FFD700]',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {paused && (
        <p className="mt-3 text-xs text-amber-300">
          ⏸️ 일시정지 중 — Landing 카운터에도 "수집 일시정지" 뱃지가 노출됩니다.
        </p>
      )}
    </Card>
  );
}

// ─── 비밀번호 변경 ──────────────────────────────────────────

function PasswordPanel() {
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => api.adminAuth.changePassword(current.trim(), next.trim()),
    onSuccess: () => {
      setMsg(
        '비밀번호가 변경되었습니다. 보안을 위해 자동 로그아웃됩니다. 새 비밀번호로 즉시 다시 로그인하세요.',
      );
      setErr(null);
      setCurrent('');
      setNext('');
      setConfirm('');
      // admin 세션 무효화 신호 — 즉시 로그인 화면으로 돌아감
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['admin', 'me'] });
      }, 1500);
    },
    onError: (e) => {
      setErr((e as Error).message);
      setMsg(null);
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    const c = current.trim();
    const n = next.trim();
    const cf = confirm.trim();
    if (!c || !n) {
      setErr('현재/새 비밀번호를 입력하세요');
      return;
    }
    if (n.length < 8) {
      setErr('새 비밀번호는 최소 8자 이상이어야 합니다');
      return;
    }
    if (n !== cf) {
      setErr('새 비밀번호와 확인 값이 다릅니다');
      return;
    }
    if (n === c) {
      setErr('새 비밀번호가 현재 비밀번호와 동일합니다');
      return;
    }
    setErr(null);
    change.mutate();
  }

  return (
    <Card>
      <CardTitle className="flex items-center gap-2">
        <Lock className="h-5 w-5 text-gold" /> 관리자 비밀번호 변경
      </CardTitle>
      <CardDescription>
        변경 즉시 적용됩니다. 다음 로그인부터 새 비밀번호가 필요해요.
      </CardDescription>

      <form className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" onSubmit={submit}>
        <Input
          type="password"
          placeholder="현재 비밀번호"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          type="password"
          placeholder="새 비밀번호 (8자 이상)"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          minLength={8}
        />
        <Input
          type="password"
          placeholder="새 비밀번호 확인"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button
          type="submit"
          className="sm:col-span-3"
          disabled={!current || !next || !confirm || change.isPending}
        >
          {change.isPending ? '변경 중…' : '비밀번호 변경'}
        </Button>
      </form>

      {msg && <p className="mt-3 text-sm text-emerald-400">{msg}</p>}
      {err && <p className="mt-3 text-sm text-red-400">{err}</p>}
    </Card>
  );
}

// ─── 운영진 임명 ───────────────────────────────────────────

function ModeratorPanel() {
  const qc = useQueryClient();
  const members = useQuery({
    queryKey: ['admin', 'members', 'all'],
    queryFn: () => adminFetch<MemberSummary[]>('/api/admin/members?status=active'),
  });

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) =>
      adminFetch<{ id: string; role: UserRole }>(`/api/admin/members/${encodeURIComponent(id)}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['mod'] });
    },
  });

  return (
    <Card>
      <CardTitle className="flex items-center gap-2">
        <UserCog className="h-5 w-5 text-gold" /> 운영진 임명 / 해임
      </CardTitle>
      <CardDescription>
        활성 회원만 표시됩니다. 부운영자(moderator)는 회원 승인만, 관리자(admin)는 모든 권한.
      </CardDescription>

      {members.isLoading && (
        <div className="mt-4 space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {members.isError && (
        <p className="mt-4 text-sm text-red-400">{(members.error as Error).message}</p>
      )}
      {members.data && members.data.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">활성 회원이 없습니다.</p>
      )}
      {members.data && members.data.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-800">
          {members.data.map((m) => (
            <li key={m.id} className="py-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white truncate">{m.nickname || m.name}</span>
                  {m.nickname && (
                    <span className="text-[11px] text-gray-500 truncate">· {m.name}</span>
                  )}
                  <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', ROLE_COLOR[m.role])}>
                    {ROLE_LABEL[m.role]}
                  </span>
                  <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_COLOR[m.status])}>
                    {STATUS_LABEL[m.status]}
                  </span>
                </div>
                <div className="text-xs text-gray-400 truncate">{m.email}</div>
              </div>
              <select
                className="h-9 rounded-lg border border-gray-800 bg-gray-900 px-3 text-sm text-white"
                value={m.role}
                onChange={(e) => setRole.mutate({ id: m.id, role: e.target.value as UserRole })}
                disabled={setRole.isPending}
              >
                <option value="member">member (일반)</option>
                <option value="moderator">moderator (부운영자)</option>
                <option value="admin">admin (관리자)</option>
              </select>
            </li>
          ))}
        </ul>
      )}
      {setRole.error && (
        <p className="mt-3 text-sm text-red-400">{(setRole.error as Error).message}</p>
      )}
    </Card>
  );
}

// ─── 풀 상태 ────────────────────────────────────────────────

function PoolPanel() {
  const q = useQuery({
    queryKey: ['admin', 'pool'],
    queryFn: () => adminFetch<PoolStatus>('/api/admin/pool'),
    refetchInterval: 15_000,
  });

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-gold" /> 키 풀 상태
        </CardTitle>
        <span className="text-xs text-gray-500">15초마다 자동 갱신</span>
      </div>

      {q.isLoading && (
        <div className="mt-4 space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}
      {q.isError && <p className="mt-4 text-sm text-red-400">{(q.error as Error).message}</p>}
      {q.data && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          <section>
            <h3 className="text-xs uppercase tracking-widest text-gray-400 mb-2">
              Ads ({q.data.ads.length}세트)
            </h3>
            <ul className="grid grid-cols-4 gap-2">
              {q.data.ads.map((e) => (
                <li
                  key={e.index}
                  className={cn(
                    'rounded-lg border px-2 py-1.5 text-xs text-center',
                    e.blocked
                      ? 'border-red-500/40 bg-red-500/10 text-red-300'
                      : 'border-gray-800 text-gray-300',
                  )}
                  title={e.blocked ?? 'ok'}
                >
                  #{e.index}
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {e.blocked ? e.blocked : 'ok'}
                  </div>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="text-xs uppercase tracking-widest text-gray-400 mb-2">
              Search ({q.data.search.length}세트)
            </h3>
            <ul className="grid grid-cols-5 sm:grid-cols-8 gap-1.5 max-h-56 overflow-y-auto">
              {q.data.search.map((e) => {
                const pct = Math.min(100, ((e.usedToday ?? 0) / 25_000) * 100);
                return (
                  <li
                    key={e.index}
                    className={cn(
                      'relative rounded-md border px-1.5 py-1 text-[10px] text-center overflow-hidden',
                      e.blocked
                        ? 'border-red-500/40 bg-red-500/10 text-red-300'
                        : 'border-gray-800 text-gray-300',
                    )}
                    title={`#${e.index} ${e.usedToday ?? 0}/25000 ${e.blocked ?? ''}`}
                  >
                    <span className="relative z-10">#{e.index}</span>
                    <div className="absolute inset-0 bg-gold/15" style={{ width: `${pct}%` }} />
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      )}
    </Card>
  );
}

// ─── 수동 큐잉 ──────────────────────────────────────────────

function RefreshPanel() {
  const [limit, setLimit] = useState(200);
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () =>
      adminFetch<{ enqueued: number }>('/api/admin/refresh/run', {
        method: 'POST',
        body: JSON.stringify({ limit }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'pool'] }),
  });

  return (
    <Card>
      <CardTitle className="flex items-center gap-2">
        <RefreshCw className="h-5 w-5 text-gold" /> 수동 갱신 큐잉
      </CardTitle>
      <CardDescription>cron 5분 사이클을 기다리지 않고 즉시 N개 큐에 push.</CardDescription>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Input
          type="number"
          className="w-32"
          value={limit}
          min={1}
          max={2000}
          step={50}
          onChange={(e) => setLimit(Math.max(1, Math.min(2000, Number(e.target.value) || 1)))}
        />
        <Button onClick={() => m.mutate()} disabled={m.isPending}>
          {m.isPending ? '큐잉 중…' : `${limit}개 큐잉`}
        </Button>
        {m.data && (
          <span className="text-sm text-emerald-400">
            ✓ {formatNumber(m.data.enqueued)}개 enqueue
          </span>
        )}
        {m.error && <span className="text-sm text-red-400">{(m.error as Error).message}</span>}
      </div>
    </Card>
  );
}

// ─── 시드 수집기 ────────────────────────────────────────────

interface SeedPreset {
  id: string;
  label: string;
  badge: string;
  desc: string;
  depth: number;
  maxKeywords: number;
  delayMs: number;
}

const SEED_PRESETS: SeedPreset[] = [
  {
    id: 'small',
    label: '소량 워밍',
    badge: '~500개',
    desc: '시드만 빠르게 등록. 회원 가입 직후 가벼운 점검용.',
    depth: 0,
    maxKeywords: 500,
    delayMs: 0,
  },
  {
    id: 'standard',
    label: '표준 시작',
    badge: '~5,000개',
    desc: '시드 + 연관 키워드 1단계까지. 챌린지 시작 시 권장.',
    depth: 1,
    maxKeywords: 5_000,
    delayMs: 50,
  },
  {
    id: 'large',
    label: '대량 확장',
    badge: '~30,000개',
    desc: '연관의 연관까지. 안정화 후, Ads 풀에 여유 있을 때.',
    depth: 2,
    maxKeywords: 30_000,
    delayMs: 100,
  },
];

function SeedPanel() {
  const [seedsText, setSeedsText] = useState('');
  const [depth, setDepth] = useState(1);
  const [maxKeywords, setMaxKeywords] = useState(5_000);
  const [delayMs, setDelayMs] = useState(50);
  const [presetId, setPresetId] = useState<string>('standard');
  const [report, setReport] = useState<SeedReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<string[] | null>(null);

  useEffect(() => {
    let active = true;
    adminFetch<{ seeds: string[] }>('/api/admin/seed/defaults')
      .then((d) => active && setDefaults(d.seeds))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  function applyPreset(p: SeedPreset) {
    setPresetId(p.id);
    setDepth(p.depth);
    setMaxKeywords(p.maxKeywords);
    setDelayMs(p.delayMs);
  }

  const m = useMutation({
    mutationFn: async () => {
      const seeds = seedsText
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload: Record<string, unknown> = { depth, maxKeywords, delayMs };
      if (seeds.length > 0) payload.seeds = seeds;
      return adminFetch<SeedReport>('/api/admin/seed', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: (r) => {
      setReport(r);
      setError(null);
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <Card>
      <CardTitle className="flex items-center gap-2">
        <Sprout className="h-5 w-5 text-gold" /> 키워드 시드 수집
      </CardTitle>
      <CardDescription>
        시드 키워드(분석 출발점)를 DB에 등록합니다. 등록된 키워드는 5분 cron이 자동으로 분석해서 점수를 매깁니다.
        시드 입력란을 비우면 미리 준비된 <strong className="text-gray-300">{defaults?.length ?? '50'}개 기본 시드</strong>
        (맥북·다이어트 보조제·캠핑 의자 등 9개 카테고리)가 사용됩니다.
      </CardDescription>

      {/* ── 프리셋 ───────────────────────────── */}
      <div className="mt-5">
        <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">⚡ 빠른 설정</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {SEED_PRESETS.map((p) => {
            const active = presetId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                className={cn(
                  'text-left rounded-xl border p-3 transition-colors',
                  active
                    ? 'border-gold bg-gold/10'
                    : 'border-gray-800 bg-gray-950 hover:border-gold/40',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-white">{p.label}</span>
                  <span className="text-[10px] font-bold text-gold">{p.badge}</span>
                </div>
                <p className="mt-1 text-xs text-gray-400">{p.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 시드 키워드 입력 ─────────────────── */}
      <div className="mt-5">
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="text-sm font-medium text-gray-200">시드 키워드 (선택)</label>
          <span className="text-xs text-gray-500">한 줄에 하나씩 · 비우면 기본 시드 사용</span>
        </div>
        <textarea
          className="h-32 w-full rounded-xl border border-gray-800 bg-gray-950 p-3 text-sm font-mono text-white placeholder:text-gray-500 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          placeholder={`예시 (직접 시드를 정하고 싶을 때):\n맥북\n다이어트\n캠핑 의자\n\n비워두면 기본 시드 ${defaults?.length ?? 50}개가 사용됩니다.`}
          value={seedsText}
          onChange={(e) => setSeedsText(e.target.value)}
        />
      </div>

      {/* ── 상세 옵션 ─────────────────────────── */}
      <details className="mt-5 group">
        <summary className="cursor-pointer text-sm text-gray-300 hover:text-gold list-none flex items-center gap-1">
          <span className="text-gold">▸</span>
          <span className="group-open:hidden">상세 옵션 (수동 조정)</span>
          <span className="hidden group-open:inline">상세 옵션 닫기</span>
        </summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <LabeledNumber
            label="탐색 깊이"
            help="0 = 시드만 등록 · 1 = 시드+연관(권장) · 2 = 연관의 연관까지 (호출 폭증)"
            value={depth}
            onChange={setDepth}
            min={0}
            max={3}
          />
          <LabeledNumber
            label="최대 등록 개수"
            help="안전망 상한. 이 수에 도달하면 즉시 중단."
            value={maxKeywords}
            onChange={setMaxKeywords}
            step={500}
          />
          <LabeledNumber
            label="호출 간 대기 (ms)"
            help="네이버 광고 API 호출 사이 대기. 50~100 권장."
            value={delayMs}
            onChange={setDelayMs}
            step={10}
          />
        </div>
      </details>

      {/* ── 실행 ───────────────────────────────── */}
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <Button
          size="lg"
          variant="gold"
          className="sm:flex-1"
          onClick={() => m.mutate()}
          disabled={m.isPending}
        >
          {m.isPending ? '수집 중… (네이버 API 호출 진행)' : `🌱 ${currentLabel(presetId)} 실행`}
        </Button>
        <p className="text-xs text-gray-500 sm:max-w-xs">
          시드 입력란이 비어 있으면 기본 시드 {defaults?.length ?? 50}개 + 위 설정으로 자동 진행.
        </p>
      </div>

      {error && (
        <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </p>
      )}

      {report && (
        <div className="mt-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-2">
            <Activity className="h-3.5 w-3.5 text-gold" /> 실행 결과
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="신규 등록 키워드" value={formatNumber(report.totalInserted)} />
            <Stat label="시드만 등록" value={formatNumber(report.seedsInserted)} />
            <Stat label="네이버 API 호출" value={formatNumber(report.apiCalls)} />
            <Stat label="실패 횟수" value={formatNumber(report.errors)} />
            <Stat label="실제 탐색 깊이" value={String(report.depthReached)} />
            <Stat label="상한 도달" value={report.capHit ? '예 (상한↑ 검토)' : '아니오'} />
            <Stat
              label="소요 시간"
              value={
                report.durationMs < 1000
                  ? `${report.durationMs} ms`
                  : `${(report.durationMs / 1000).toFixed(1)} 초`
              }
            />
          </div>
          <p className="mt-3 text-xs text-gray-500">
            등록 직후 5분 cron이 자동으로 분석 큐에 넣습니다. 즉시 시작하고 싶으면 위의 "수동 갱신 큐잉"에서 실행하세요.
          </p>
        </div>
      )}
    </Card>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step = 1,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-400">{label}</span>
      <Input
        type="number"
        className="mt-1"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </label>
  );
}

function LabeledNumber({
  label,
  help,
  value,
  onChange,
  step = 1,
  min,
  max,
}: {
  label: string;
  help: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-200">{label}</span>
      <Input
        type="number"
        className="mt-1"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
      <span className="mt-1 block text-[11px] text-gray-500 leading-snug">{help}</span>
    </label>
  );
}

function currentLabel(id: string): string {
  if (id === 'small') return '소량 워밍';
  if (id === 'standard') return '표준 시작';
  if (id === 'large') return '대량 확장';
  return '수동 설정';
}
